import React from 'react';

import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';

import { useUpdateAccountingRange } from 'src/features/accountingRanges/api/useUpdateAccountingRange';
import { LIST_ACCOUNTING_RANGES_RESOURCE } from 'src/features/accountingRanges/api/useListAccountingRanges';
import AccountingRangeFormModal from 'src/features/accountingRanges/components/forms/AccountingRangeFormModal';
import { AccountingRange } from 'src/features/accountingRanges/utils/types';
import { accountingRangesMessages } from 'src/features/accountingRanges/utils/messages';
import { ACCOUNTING_RANGE_RESOURCE } from 'src/features/accountingRanges/api/useGetAccountingRange';

interface UpdateAccountingRangeFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAccountingRange: AccountingRange;
    children?: React.ReactNode;
}

function UpdateAccountingRangeFormModal<TData>({
                                                   className,
                                                   semantics,
                                                   children,
                                                   selectedAccountingRange,
                                               }: UpdateAccountingRangeFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { mutate, isPending } = useUpdateAccountingRange({
        onSuccess: ({ name }) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ACCOUNTING_RANGES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [ACCOUNTING_RANGE_RESOURCE] });
            toast({
                title: formatMessage(accountingRangesMessages.updateAccountingRangeSuccess),
                description: formatMessage(accountingRangesMessages.updateAccountingRangeSuccessDescription, {
                    entity: name,
                }),
            });
        },
    });

    return (
        <AccountingRangeFormModal
            selectedAccountingRange={selectedAccountingRange}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ ...values, id: selectedAccountingRange.id }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(accountingRangesMessages.editAccountingRangeSubtitle, {
                entity: selectedAccountingRange.name,
            })}
        >
            {children}
        </AccountingRangeFormModal>
    );
}

export default UpdateAccountingRangeFormModal;
